import { useState, useEffect } from 'react';
import { PhoneFilters, PhoneBookEntry, ShivarOption } from '../../types';
import { useContacts } from '../../hooks/useContacts';
import { FilterBar } from '../../components/FilterBar';
import { PhoneBookTable } from '../../components/PhoneBookTable';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { formatShivar } from '../../utils/shivar';

export const AdminContacts = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<PhoneFilters>({});
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [options, setOptions] = useState({ shivars: [] as ShivarOption[] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [creatorMap, setCreatorMap] = useState<Record<string, string>>({});

  const fetchOptions = async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      const { data, error: dbError } = await supabase.from('shivars').select('name, marathi_name').order('name');
      if (dbError) throw dbError;
      setOptions({
        shivars: (data ?? []).map((s) => ({ value: s.name, label: formatShivar(s.name, s.marathi_name) })),
      });
    } catch (err) {
      setOptionsError(err instanceof Error ? err.message : 'Failed to load shivars');
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const { contacts, count, loading, error, pageSize } = useContacts({ ...filters, page, includePrivate: true, refresh });
  const totalPages = Math.ceil(count / pageSize);
  const shivarMap = Object.fromEntries(options.shivars.map((s) => [s.value, s.label]));

  useEffect(() => {
    const loadCreators = async () => {
      const ids = [...new Set(contacts.map((c) => c.created_by).filter(Boolean))] as string[];
      if (ids.length === 0) {
        setCreatorMap({});
        return;
      }
      const { data, error: dbError } = await supabase.from('profiles').select('id, full_name').in('id', ids);
      if (dbError) return;
      const map: Record<string, string> = {};
      (data ?? []).forEach((p) => {
        map[p.id] = p.full_name ?? 'Unknown';
      });
      setCreatorMap(map);
    };
    loadCreators();
  }, [contacts]);

  const handleStatusChange = async (id: string, status: 'UNDER_REVIEW' | 'APPROVED' | 'BLOCKED') => {
    const { error: updateError } = await supabase
      .from('phone_book')
      .update({ status, approved_by: status === 'APPROVED' ? user?.id : null, approved_at: status === 'APPROVED' ? new Date().toISOString() : null })
      .eq('id', id);
    if (updateError) {
      // surface error via page reload isn't ideal, but keeps UI simple
      window.alert(updateError.message);
      return;
    }
    setRefresh((r) => r + 1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
        <p className="text-sm text-gray-600">Manage phone book entries.</p>
      </div>
      {optionsError && <ErrorAlert message={optionsError} onRetry={fetchOptions} />}
      {optionsLoading ? <Loading /> : <FilterBar filters={filters} onChange={(f) => { setFilters(f); setPage(1); }} options={options} includeAdminFilters />}
      {error && <ErrorAlert message={error} onRetry={() => window.location.reload()} />}
      {loading ? (
        <Loading />
      ) : contacts.length === 0 ? (
        <Empty title="No contacts found" message="Try adjusting your search or filters." />
      ) : (
        <PhoneBookTable contacts={contacts as PhoneBookEntry[]} shivarMap={shivarMap} showStatus showCreatedBy creatorMap={creatorMap} onStatusChange={handleStatusChange} />
      )}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button
            onClick={() => setPage((p: number) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-2 py-1 text-sm text-gray-700">{page} / {totalPages}</span>
          <button
            onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
